import { Controller } from "@hotwired/stimulus"
import { PreviewMessageService } from "../services/preview_message_service"

// remotion-preview — generic live wizard preview. Embeds the REAL composition in
// a @remotion/player iframe and feeds it the template's ConfigBuilder props,
// re-fetching props (with the step form's UNSAVED values) on every edit and
// pushing them via postMessage. Generalises the per-template music controllers;
// any kit template opts in via Common::RemotionPreviewPanelComponent.
//
// Values:
//   origin       — the remotion-service origin (postMessage target)
//   propsUrl     — POST endpoint that returns fresh ConfigBuilder props
//   props        — initial props (the saved config)
//   fieldPrefix  — only edits to inputs whose name starts with this refresh
const DEBOUNCE_MS = 350

export default class extends Controller {
  static targets = ["iframe", "error", "fallback"]
  static values = { origin: String, propsUrl: String, props: Object, fieldPrefix: String }

  connect() {
    this.service = new PreviewMessageService(this.iframeTarget, this.originValue)
    this.debounceTimer = null
    this.onEdit = this.onEdit.bind(this)
    this.onMessage = this.onMessage.bind(this)

    // Seed the player with the saved-config props once it loads (and shortly
    // after, in case the load already fired).
    this.iframeTarget.addEventListener("load", () => this.send(this.propsValue))
    this.sendTimer = setTimeout(() => this.send(this.propsValue), DEBOUNCE_MS)

    window.addEventListener("message", this.onMessage)
    document.addEventListener("input", this.onEdit)
    document.addEventListener("change", this.onEdit)
  }

  disconnect() {
    window.removeEventListener("message", this.onMessage)
    document.removeEventListener("input", this.onEdit)
    document.removeEventListener("change", this.onEdit)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.sendTimer) clearTimeout(this.sendTimer)
  }

  // The preview page posts PREVIEW_READY once it's served + mounted — hide the
  // "needs the render service" fallback. If :3005 is down it never arrives, so
  // the fallback stays visible.
  onMessage(event) {
    if (event.data && event.data.type === "PREVIEW_READY" && this.hasFallbackTarget) {
      this.fallbackTarget.style.display = "none"
    }
  }

  onEdit(event) {
    const name = event.target && event.target.name
    if (!name || !this.fieldPrefixValue || !name.startsWith(this.fieldPrefixValue)) return
    this.scheduleRefresh()
  }

  scheduleRefresh() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.refresh(), DEBOUNCE_MS)
  }

  async refresh() {
    const form = document.querySelector("[data-preview-form]")
    if (!form) return
    const body = new FormData(form)
    body.delete("_method") // POST to preview_props, don't let Rails see a PATCH
    try {
      const res = await fetch(this.propsUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        this.showError(data.error || "Couldn't build the preview — check your inputs.")
        return
      }
      this.clearError()
      this.send(await res.json())
    } catch (_error) {
      // A network hiccup is transient — keep the last good preview, no error.
    }
  }

  send(props) {
    if (props && typeof props === "object" && Object.keys(props).length > 0) {
      this.service.setProps(this.absolutizeAssetUrls(props))
    }
  }

  // The @remotion/player iframe is served from a DIFFERENT origin (the :3005 dev
  // service), so app-relative asset paths (/storage/… for generated/uploaded
  // images, /rails/active_storage/…) won't resolve against it and render blank.
  // Rewrite them to absolute URLs on the app's own origin — which the browser
  // knows, so it's correct for the multi-domain hosts (shortformly.localhost vs
  // studio.multifari.com). Same-origin (prod) is unaffected: origin + path is the
  // same URL. Only root-relative app asset paths are touched; absolute/other URLs
  // pass through untouched.
  absolutizeAssetUrls(props) {
    const origin = window.location.origin
    const rewrite = (value) => {
      if (typeof value === "string") {
        return /^\/(storage|rails)\//.test(value) ? origin + value : value
      }
      if (Array.isArray(value)) return value.map(rewrite)
      if (value && typeof value === "object") {
        const out = {}
        for (const key in value) out[key] = rewrite(value[key])
        return out
      }
      return value
    }
    return rewrite(props)
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
